import React, { useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import { Box, styled } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import MenuItem from '@mui/material/MenuItem';
import MuiTextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import DialogContent from '@mui/material/DialogContent';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import { object, string, ValidationError } from 'yup';

import Axios from '../../../Assets/Axios/Axios';

import { TypoSerif } from '../../../Components/Typo';

import { COLORS } from '../../../Constant/COLORS';

import { useToastContext } from '../../../Context/ToastContext';

import { getClearedErrorObj } from '../../../Helpers/index';

import { CreateOrder } from '../../../Server/Mutation/index';

import ThankYouModal from './ThankYou';

const BookYourDate = ({ onClose, serviceId, servicePackages, ...props }) => {
  const { showToastMessage } = useToastContext();

  const [isThankYouModalOpen, setIsThankYouModalOpen] = useState(false);
  const [inputs, setInputs] = useState({
    place: '',
    date: '',
    time: '',
    note: '',
    servicePackage: '',
  });
  const [errors, setErrors] = useState({
    place: null,
    date: null,
    time: null,
    note: null,
    servicePackage: null,
  });

  const selectedServicePackage = useMemo(() => {
    if (servicePackages !== null)
      return servicePackages.find(servicePackage => servicePackage.id === inputs.servicePackage);
    return null;
  }, [inputs.servicePackage, servicePackages]);

  function handleChange(event) {
    const { value, name } = event.target;
    setInputs(currentState => ({ ...currentState, [name]: value }));
  }

  function handleClose() {
    onClose();
    setInputs(currentStates =>
      Object.fromEntries(Object.keys(currentStates).map(key => [key, '']))
    );
    setErrors(currentErrors => getClearedErrorObj(currentErrors));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setErrors(currentErrors => getClearedErrorObj(currentErrors));
    try {
      const validatedData = await schema.validate(inputs, {
        abortEarly: false,
      });

      if (!serviceId) {
        showToastMessage('Something went wrong while trying to create your order');
        return undefined;
      }

      const { errors } = await Axios(CreateOrder({
          ...validatedData,
          serviceId,
          servicePackageId: selectedServicePackage?.id,
          total: selectedServicePackage?.price,
        }));

      if (errors) {
        showToastMessage('Something went wrong while trying to create your order');
        return undefined;
      }

      handleClose();
      setIsThankYouModalOpen(true);
    } catch (error) {
      if (error instanceof ValidationError) {
        setErrors(currentErrors => ({
          ...currentErrors,
          ...error.inner.reduce((preVal, curVal) => {
            preVal[curVal.path] = curVal.message;
            return preVal;
          }, {}),
        }));
      }
    }
  }

  return (
    <>
      <Dialog
        PaperProps={{
          sx: {
            backgroundColor: 'white',
            p: { md: '2em 3em 6em' },
            boxSizing: 'border-box',
            borderRadius: { xs: '12px', md: '40px' },
          },
        }}
        maxWidth='sm'
        onClose={handleClose}
        {...props}>
        <DialogContent>
          <IconButton onClick={onClose} sx={{ float: 'left' }}>
            <ChevronLeftIcon />
          </IconButton>

          <TypoSerif textAlign='center' variant='h4' mb={3}>
            Book your Date
          </TypoSerif>

          <Form onSubmit={handleSubmit}>
            <TextField
              sx={{ gridColumn: 'span 2' }}
              fullWidth
              placeholder='Place'
              name='place'
              value={inputs.place}
              onChange={handleChange}
              error={Boolean(errors.place)}
              helperText={errors.place}
            />

            <TextField
              fullWidth
              placeholder='Date'
              type='date'
              name='date'
              value={inputs.date}
              onChange={handleChange}
              error={Boolean(errors.date)}
              helperText={errors.date}
              inputProps={{ min: Intl.DateTimeFormat('af').format(new Date()) }}
            />

            <TextField
              fullWidth
              placeholder='time'
              type='time'
              name='time'
              value={inputs.time}
              onChange={handleChange}
              error={Boolean(errors.time)}
              helperText={errors.time}
            />

            {servicePackages && (
              <TextField
                sx={{ gridColumn: 'span 2' }}
                fullWidth
                select
                onChange={handleChange}
                name='servicePackage'
                value={inputs.servicePackage}
                error={Boolean(errors.servicePackage)}
                size='small'
                helperText={errors.servicePackage}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: value =>
                    value ? (
                      selectedServicePackage?.name
                    ) : (
                      <div style={{ color: 'grey' }}>Package</div>
                    ),
                }}>
                {servicePackages.map(servicePackage => (
                  <MenuItem key={servicePackage.id} value={servicePackage.id}>
                    {servicePackage.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              sx={{ gridColumn: 'span 2' }}
              fullWidth
              multiline
              rows={5}
              placeholder='Note'
              name='note'
              value={inputs.note}
              onChange={handleChange}
              error={Boolean(errors.note)}
              helperText={errors.note}
            />

            <Box gridColumn='span 2' display='grid' gridTemplateColumns='1fr 3fr' gap={2}>
              <Button color='blue' variant='outlined' onClick={handleClose}>
                Cancel
              </Button>
              <Button type='submit' color='blue' sx={{ color: 'white', fontWeight: 500 }}>
                Book
              </Button>
            </Box>
          </Form>
        </DialogContent>
      </Dialog>

      <ThankYouModal open={isThankYouModalOpen} onClose={() => setIsThankYouModalOpen(false)} />
    </>
  );
};

const TextField = styled(MuiTextField)({
  '.MuiOutlinedInput-root': {
    backgroundColor: COLORS.white3,
    borderRadius: '8px',
  },
});

const Form = styled('form')({
  display: 'grid',
  columnGap: '1em',
  gridTemplateColumns: 'repeat(2,1fr)',
  '& .MuiFormControl-root': { marginBottom: '2em' },
});

const schema = object({
  place: string().trim().required().label('Place'),
  date: string()
    .required()
    .transform(value => value && new Date(value).toISOString())
    .label('Date'),
  time: string()
    .required()
    .transform(value => {
      if (!value) return value;

      const [hours, minutes] = value.split(':');
      const date = new Date();
      date.setUTCHours(hours, minutes, 0, 0);

      return date.toISOString();
    })
    .label('Time'),
  note: string().trim().required().label('Note'),
  servicePackage: string().trim().required().label('Package'),
});

BookYourDate.propTypes = {
  onClose: PropTypes.func,
  serviceId: PropTypes.string,
  servicePackages: PropTypes.array,
};

export default BookYourDate;
